import React, { memo } from "react";
import PropTypes from "prop-types";
import { Grid, Card, CardContent, Typography, Box } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Label,
  Cell,
} from "recharts";
import { Pie } from "react-chartjs-2";

/* ──────────────────────────────
 ✅ 1. Staffing Balance (Pie)
──────────────────────────────── */
const StaffingBalanceChart = memo(({ t, staffingData }) => (
  <Grid item xs={12} md={4}>
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="subtitle1">
          {t("user.manpowerPlanningAnalysis.staffingBalance")}
        </Typography>
        <Box sx={{ height: { xs: 200, md: 250 } }}>
          <Pie
            data={staffingData}
            options={{
              maintainAspectRatio: false,
              responsive: true,
            }}
          />
        </Box>
      </CardContent>
    </Card>
  </Grid>
));

StaffingBalanceChart.propTypes = {
  t: PropTypes.func.isRequired,
  staffingData: PropTypes.arrayOf(PropTypes.object).isRequired,
};

/* ──────────────────────────────
 ✅ 2. Workload Distribution (Bar)
──────────────────────────────── */
const WorkloadDistributionChart = memo(({ t, workloadRechartData }) => (
  <Grid item xs={12} md={8}>
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="subtitle1">
          {t("user.manpowerPlanningAnalysis.workloadDistribution")}
        </Typography>

        <Box
          sx={{
            height: { xs: 250, md: 300 },
            overflowX: "auto",
            overflowY: "hidden",
            whiteSpace: "nowrap",
            pb: 4,
          }}
        >
          <Box
            sx={{
              display: "inline-block",
              minWidth: `${workloadRechartData.length * 120}px`,
              height: "100%",
            }}
          >
            {workloadRechartData?.length > 0 && (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={workloadRechartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} height={60} />
                  <YAxis
                    label={{
                      value: t("user.manpowerPlanningAnalysis.chart.workloadPer"),
                      angle: -90,
                      position: "insideLeft",
                      offset: 5,
                      style: {
                        textAnchor: "middle",
                        fontSize: 14,
                        fill: "#333",
                        fontWeight: 500,
                      },
                    }}
                  />
                  <Tooltip
                    formatter={(val) => [
                      `${val}%`,
                      t("user.manpowerPlanningAnalysis.chart.workload"),
                    ]}
                  />
                  <Bar
                    dataKey="pct"
                    label={{
                      position: "top",
                      formatter: (value) => `${value.toLocaleString()}%`,
                    }}
                  >
                    {workloadRechartData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Box>

        {/* ✅ Legend */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: { xs: 1.5, sm: 2, md: 3 },
            mt: 2,
            flexWrap: "wrap",
            textAlign: "center",
          }}
        >
          {[
            { color: "#ef5350", label: t("analysis.overworked") },
            { color: "#108092", label: t("analysis.balanced") },
            { color: "#ffa726", label: t("analysis.underworked") },
          ].map(({ color, label }) => (
            <Box key={color} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 14, height: 14, backgroundColor: color, borderRadius: "2px" }} />
              <Typography variant="body2">{label}</Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  </Grid>
));

WorkloadDistributionChart.propTypes = {
  t: PropTypes.func.isRequired,
  workloadRechartData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      pct: PropTypes.number,
      color: PropTypes.string,
    })
  ).isRequired,
};

/* ──────────────────────────────
 ✅ 3. Cost per FTE Chart
──────────────────────────────── */
const CostPerFteChart = memo(({ t, costPerFteData }) => (
  <Grid item xs={12} md={8} lg={8}>
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="subtitle1">
          {t("user.manpowerPlanningAnalysis.chart.costFte")}
        </Typography>

        <Box sx={{ height: { xs: 250, md: 300 }, overflowX: "auto", pb: 6 }}>
          <Box sx={{ minWidth: `${costPerFteData.length * 100}px`, height: "100%" }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={costPerFteData} margin={{ top: 20, right: 30, left: 20, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="jobTitle" tick={{ angle: -45, textAnchor: "end" }} />
                <YAxis />
                <Tooltip
                  formatter={(val) => [
                    val.toLocaleString(),
                    t("user.manpowerPlanningAnalysis.chart.cost"),
                  ]}
                />
                <Bar
                  dataKey="cost"
                  fill="rgba(54, 162, 235, 0.6)"
                  maxBarSize={40}
                  label={{
                    position: "top",
                    formatter: (value) => `${value.toLocaleString()} `,
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
        <Box sx={{ textAlign: "center", mt: 1 }}>
          <Typography variant="subtitle2">
            {t("user.manpowerPlanningAnalysis.chart.jobName")}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  </Grid>
));

CostPerFteChart.propTypes = {
  t: PropTypes.func.isRequired,
  costPerFteData: PropTypes.arrayOf(
    PropTypes.shape({
      jobTitle: PropTypes.string.isRequired,
      cost: PropTypes.number.isRequired,
    })
  ).isRequired,
};

/* ──────────────────────────────
 ✅ 4. Opex vs Revenue Chart
──────────────────────────────── */
const OpexRevenueChart = memo(({ t, opexRevenueData }) => (
  <Grid item xs={12} md={4} lg={4}>
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="subtitle1">
          {t("user.manpowerPlanningAnalysis.chart.opexRev")}
        </Typography>

        {/* {opexRevenueData?.length === 2 && (
          <Typography
            variant="h6"
            sx={{
              textAlign: "center",
              mt: 1,
              color:
                opexRevenueData[0].amount / opexRevenueData[1].amount > 1 ? "#ef5350" : "#66bb6a",
            }}
          >
            {t("user.manpowerPlanningAnalysis.chart.opexToRevenue")}{" "}
            {(
              (opexRevenueData.find((d) => d.name === "Opex")?.amount /
                opexRevenueData.find((d) => d.name === "Revenue")?.amount) *
              100
            ).toFixed(1)}
            %
          </Typography>
        )} */}

{opexRevenueData?.length === 2 && (() => {
  const opex = opexRevenueData.find(d => d.name === "Opex")?.amount;
  const revenue = opexRevenueData.find(d => d.name === "Revenue")?.amount;

  const isValid =
    typeof opex === "number" &&
    typeof revenue === "number" &&
    !Number.isNaN(opex) &&
    !Number.isNaN(revenue) &&
    revenue > 0;

  const ratio = isValid ? opex / revenue : null;

  return (
    <Typography
      variant="h6"
      sx={{
        textAlign: "center",
        mt: 1,
        color: isValid
          ? ratio > 1
            ? "#ef5350"
            : "#66bb6a"
          : "text.secondary",
      }}
    >
      {t("user.manpowerPlanningAnalysis.chart.opexToRevenue")}{" "}
      {isValid ? `${(ratio * 100).toFixed(1)}%` : t("")}
    </Typography>
  );
})()}


        <Box sx={{ height: { xs: 300, md: 350 } }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              layout="vertical"
              data={opexRevenueData}
              margin={{ top: 5, right: 30, left: 40, bottom: 40 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tickFormatter={(value) => `${(value / 1000).toLocaleString()}k`}>
                <Label
                  value={t("user.manpowerPlanningAnalysis.chart.amount2")}
                  position="bottom"
                  offset={5}
                />
              </XAxis>
              <YAxis type="category" dataKey="name" />
              <Tooltip
                formatter={(value) => [
                  value.toLocaleString(),
                  t("user.manpowerPlanningAnalysis.chart.amount2"),
                ]}
              />
              <Bar
                dataKey="amount"
                radius={6}
                barSize={20}
                fill={(entry) => (entry.name === "Opex" ? "#ef5350" : "#66bb6a")}
                label={{
                  position: "right",
                  formatter: (value) => `${value.toLocaleString()} `,
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  </Grid>
));

OpexRevenueChart.propTypes = {
  t: PropTypes.func.isRequired,
  opexRevenueData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
    })
  ).isRequired,
};

/* ──────────────────────────────
 ✅ Export
──────────────────────────────── */
export { StaffingBalanceChart, WorkloadDistributionChart, CostPerFteChart, OpexRevenueChart };
